import {useEffect, useState, type RefObject} from 'react';

/** Matches the `md` breakpoint (tokens.ts / plain `_mixins.scss`). */
const DESKTOP_MIN_WIDTH_PX = 900;

/**
 * Whether a self-rotating section may advance on its own: only at desktop widths, only while
 * the section is on screen, and never for visitors who asked for reduced motion.
 *
 * On phones these sections stack, so a rotation changes their height and moves everything
 * below without any input — Google counts that as layout shift (CLS). Off screen, the timer's
 * renders are pure main-thread cost that can delay a tap elsewhere (INP).
 *
 * Starts `false`, so the server render and the first client render agree.
 */
export function useAutoplayAllowed(ref: RefObject<Element>, minWidth = DESKTOP_MIN_WIDTH_PX): boolean {
    const [wide, setWide] = useState(false);
    const [onScreen, setOnScreen] = useState(false);

    useEffect(() => {
        if (globalThis.window === undefined || !globalThis.matchMedia) return undefined;
        const query = globalThis.matchMedia(`(min-width: ${minWidth}px) and (prefers-reduced-motion: no-preference)`);
        const sync = () => setWide(query.matches);
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, [minWidth]);

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting));
        observer.observe(node);
        return () => observer.disconnect();
    }, [ref]);

    return wide && onScreen;
}
